import { AppState } from './AppState';
import type { ARController } from '../ar/ARController';
import { RecordingService, type RecordingResult } from '../recording/RecordingService';
import { AnalyticsService, EVENTS } from '../analytics/AnalyticsService';
import { UIController } from '../ui/UIController';
import { detectProfile } from '../performance/DeviceProfiler';
import { requestCameraPermission } from '../utils/permissions';
import { assetExists, loadSound } from '../utils/assetLoader';
import {
  canShareFile,
  detectInAppBrowser,
  hasCameraSupport,
  isSecureContextForCamera,
} from '../utils/browserSupport';
import type { PerformanceProfile } from '../performance/PerformanceProfile';

const TARGET_SRC = import.meta.env.VITE_TARGET_SRC ?? '/targets/daemon-can.mind';
const MODEL_SRC = import.meta.env.VITE_MODEL_SRC ?? '/models/daemon-can.glb';
const SOUND_SRC = import.meta.env.VITE_SCAN_SOUND_SRC ?? '/audio/scan-success.mp3';
const MAX_RECORDING_SECONDS = Number(import.meta.env.VITE_MAX_RECORDING_SECONDS ?? 30);

/**
 * Orchestrateur : détient l'état, coordonne AR, enregistrement, analytics et UI.
 */
export class App {
  private readonly state = new AppState();
  private readonly analytics: AnalyticsService;
  /** Chargé paresseusement (MindAR + TFJS ≈ 2 Mo) au clic « Activer la caméra ». */
  private ar: ARController | null = null;
  private readonly recording = new RecordingService();
  private readonly ui: UIController;
  private readonly profile: PerformanceProfile;
  private readonly sound = loadSound(SOUND_SRC);

  private arStarted = false;
  private soundPlayed = false;
  private recordingTimer = 0;
  private recordingStartedAt = 0;
  private lastResult: RecordingResult | null = null;
  private previewUrl: string | null = null;

  constructor(analytics: AnalyticsService) {
    this.analytics = analytics;
    this.profile = detectProfile();

    this.ui = new UIController({
      onStartCamera: () => void this.startExperience(),
      onRecord: () => void this.startRecording(),
      onStopRecording: () => void this.stopRecording(),
      onDownload: () => this.download(),
      onShare: () => void this.share(),
      onRestart: () => this.restart(),
      onRetry: () => this.retry(),
    });
  }

  init(): void {
    const inApp = detectInAppBrowser();
    this.analytics.track(EVENTS.PAGE_OPEN, {
      inAppBrowser: inApp.inApp,
      inAppName: inApp.app ?? null,
      profile: this.profile.name,
    });

    if (inApp.inApp) {
      this.ui.showInAppBrowserNotice(inApp.app ?? 'application');
    }

    // Économie d'énergie : rendu suspendu quand l'onglet est masqué
    document.addEventListener('visibilitychange', () => {
      if (this.arStarted) this.ar?.setPaused(document.hidden);
    });

    this.ui.showScreen('landing');
  }

  // ── Démarrage caméra + AR ─────────────────────────────────

  private async startExperience(): Promise<void> {
    if (!this.state.transition('requesting-camera')) return;

    if (!isSecureContextForCamera()) {
      return this.fail(
        'Connexion non sécurisée',
        "La caméra nécessite une page en HTTPS. Ouvrez l'URL sécurisée de l'expérience.",
        'insecure_context',
      );
    }
    if (!hasCameraSupport()) {
      return this.fail(
        'Navigateur non supporté',
        "Ce navigateur ne donne pas accès à la caméra. Ouvrez la page dans Safari (iPhone) ou Chrome (Android).",
        'no_camera_api',
      );
    }

    this.ui.showScreen('loading');
    this.ui.setLoadingMessage("Autorisez l'accès à la caméra pour scanner la canette.");

    const permission = await requestCameraPermission();
    if (!permission.granted) {
      this.analytics.track(EVENTS.CAMERA_DENIED, { reason: permission.reason });
      const message =
        permission.reason === 'denied'
          ? "L'accès à la caméra a été refusé. Autorisez la caméra pour ce site dans les réglages du navigateur, puis réessayez."
          : "Aucune caméra utilisable n'a été trouvée sur cet appareil.";
      return this.fail('Caméra indisponible', message, `camera_${permission.reason}`);
    }
    this.analytics.track(EVENTS.CAMERA_GRANTED);

    if (!this.state.transition('loading')) return;
    this.ui.setLoadingMessage("Chargement de l'expérience…");

    if (!(await assetExists(TARGET_SRC))) {
      return this.fail(
        'Cible de scan manquante',
        `Le fichier cible ${TARGET_SRC} est introuvable. Voir le README pour générer la cible MindAR.`,
        'target_missing',
      );
    }

    try {
      // Import dynamique : la landing reste légère, le moteur AR arrive ici
      const { ARController } = await import('../ar/ARController');
      this.ar = new ARController();
      const { usedFallbackModel } = await this.ar.init(
        this.ui.arContainer,
        TARGET_SRC,
        MODEL_SRC,
        this.profile,
        {
          onTargetFound: () => this.onTargetFound(),
          onTargetLost: () => this.onTargetLost(),
        },
      );
      if (usedFallbackModel) {
        this.ui.showToast('Modèle 3D de démonstration affiché (daemon-can.glb manquant).');
      }
      await this.ar.start();
      this.arStarted = true;
    } catch (err) {
      console.error('[ar] Démarrage impossible', err);
      return this.fail(
        "Impossible de démarrer l'AR",
        "Le moteur de réalité augmentée n'a pas pu démarrer. Vérifiez la cible .mind et rechargez la page.",
        'ar_start_failed',
      );
    }

    this.state.transition('searching');
    this.analytics.track(EVENTS.SCAN_STARTED);
    this.ui.showScreen('ar');
    this.ui.setScanMode('searching');
  }

  // ── Tracking ──────────────────────────────────────────────

  private onTargetFound(): void {
    this.analytics.track(EVENTS.TARGET_FOUND);
    if (this.state.state === 'recording') {
      this.ui.showTrackingLostHint(false);
      return;
    }
    if (!this.state.transition('tracking')) return;
    if (!this.soundPlayed) {
      this.sound.play(); // une seule fois par session
      this.soundPlayed = true;
    }
    this.ui.setScanMode('tracking');
  }

  private onTargetLost(): void {
    this.analytics.track(EVENTS.TARGET_LOST);
    if (this.state.state === 'recording') {
      // On n'interrompt pas la vidéo : simple indication visuelle
      this.ui.showTrackingLostHint(true);
      return;
    }
    if (this.state.transition('searching')) {
      this.ui.setScanMode('searching');
    }
  }

  // ── Enregistrement ────────────────────────────────────────

  private async startRecording(): Promise<void> {
    const capability = this.recording.detectCapability();

    if (capability === 'none') {
      this.ui.showToast(this.recording.fallbackMessage(), 7000);
      return;
    }

    if (!this.ar) return;

    if (capability === 'screenshot') {
      // Fallback : capture photo composite + explication honnête
      try {
        const result = await this.recording.captureScreenshot(
          this.ar.videoElement,
          this.ar.glCanvas,
          this.profile,
        );
        this.analytics.track(EVENTS.SCREENSHOT_CAPTURED);
        this.ui.showToast(this.recording.fallbackMessage(), 7000);
        this.openPreview(result);
      } catch {
        this.ui.showToast(this.recording.fallbackMessage(), 7000);
      }
      return;
    }

    if (!this.state.transition('recording')) return;
    try {
      this.recording.startVideo(this.ar.videoElement, this.ar.glCanvas, this.profile);
    } catch (err) {
      console.error('[recording]', err);
      this.state.transition('tracking');
      this.ui.showToast(this.recording.fallbackMessage(), 7000);
      return;
    }

    this.analytics.track(EVENTS.RECORDING_STARTED, { profile: this.profile.name });
    this.ui.setScanMode('recording');
    this.ui.updateTimer(0);
    this.recordingStartedAt = performance.now();
    this.recordingTimer = window.setInterval(() => {
      const seconds = (performance.now() - this.recordingStartedAt) / 1000;
      this.ui.updateTimer(seconds);
      if (seconds >= MAX_RECORDING_SECONDS) void this.stopRecording();
    }, 250);
  }

  private async stopRecording(): Promise<void> {
    if (this.state.state !== 'recording') return;
    window.clearInterval(this.recordingTimer);

    let result: RecordingResult;
    try {
      result = await this.recording.stopVideo();
    } catch (err) {
      console.error('[recording]', err);
      this.state.transition('tracking');
      this.ui.setScanMode('tracking');
      this.ui.showToast("L'enregistrement a échoué. Réessayez.");
      return;
    }

    const durationSec = Math.round((performance.now() - this.recordingStartedAt) / 1000);
    this.analytics.track(EVENTS.RECORDING_STOPPED, {
      durationSec,
      sizeKB: Math.round(result.blob.size / 1024),
      mimeType: result.mimeType,
    });

    this.state.transition('preview');
    this.openPreview(result);
  }

  private openPreview(result: RecordingResult): void {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.lastResult = result;
    this.previewUrl = URL.createObjectURL(result.blob);

    const file = new File([result.blob], result.fileName, { type: result.mimeType });
    this.ui.showPreview(this.previewUrl, {
      isImage: result.mimeType.startsWith('image/'),
      canShare: canShareFile(file),
    });
    // Si on vient du fallback screenshot, l'état peut encore être 'tracking'
    if (this.state.state === 'tracking') this.state.transition('preview');
  }

  // ── Actions preview ───────────────────────────────────────

  private download(): void {
    if (!this.lastResult || !this.previewUrl) return;
    const a = document.createElement('a');
    a.href = this.previewUrl;
    a.download = this.lastResult.fileName;
    a.click();
    this.analytics.track(EVENTS.VIDEO_DOWNLOADED, { mimeType: this.lastResult.mimeType });
  }

  private async share(): Promise<void> {
    if (!this.lastResult) return;
    const file = new File([this.lastResult.blob], this.lastResult.fileName, {
      type: this.lastResult.mimeType,
    });
    try {
      await navigator.share({ files: [file], title: 'Mon scan DÆMON' });
      this.analytics.track(EVENTS.VIDEO_SHARED);
    } catch (err) {
      // AbortError = l'utilisateur a annulé : silencieux
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        this.ui.showToast("Le partage n'est pas disponible ici. Utilisez Télécharger.");
      }
    }
  }

  private restart(): void {
    this.ui.clearPreview();
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
    this.lastResult = null;
    // L'AR tourne toujours en arrière-plan : on revient au scan
    this.state.transition('searching');
    this.ui.showScreen('ar');
    this.ui.setScanMode('searching');
  }

  private retry(): void {
    if (this.state.state === 'error') {
      this.state.transition('landing');
      this.ui.showScreen('landing');
    }
  }

  // ── Erreurs ───────────────────────────────────────────────

  private fail(title: string, message: string, code: string): void {
    this.analytics.track(EVENTS.ERROR, { code });
    this.state.transition('error');
    this.ui.showError(title, message);
  }
}
